import type { App, DirectiveBinding } from 'vue';

import {
  splitTextForSearchHighlight,
  MARK_CLASS
} from '@satellite/helpers/splitTextForSearchHighlight';

export type SearchHighlightDirectiveValue = {
  text: string;
  searchQuery?: string;
};

function apply(
  el: HTMLElement,
  binding: DirectiveBinding<SearchHighlightDirectiveValue | undefined>
) {
  const payload = binding.value;
  const text = payload?.text ?? '';
  const query = (payload?.searchQuery ?? '').trim();

  if (!query) {
    const hasOnlyTextChildNodes =
      el.childNodes.length === 0 ||
      Array.from(el.childNodes).every(n => n.nodeType === Node.TEXT_NODE);

    if (!el.querySelector('mark') && el.textContent === text && hasOnlyTextChildNodes) {
      return;
    }
    el.textContent = text;
    return;
  }

  el.textContent = '';
  for (const seg of splitTextForSearchHighlight(text, query)) {
    if (seg.highlight) {
      const mark = document.createElement('mark');
      mark.className = MARK_CLASS;
      mark.textContent = seg.text;
      el.appendChild(mark);
    } else {
      el.appendChild(document.createTextNode(seg.text));
    }
  }
}

/**
 * Highlights `searchQuery` inside `text` on the host element using
 * {@link splitTextForSearchHighlight} (no RegExp from user input).
 *
 * **Register** (e.g. app bootstrap):
 * `app.directive('search-highlight', vSearchHighlight)`
 * then in templates: `v-search-highlight="{ text: label, searchQuery }"`.
 *
 * **Note:** directives are not forwarded through `v-bind="$attrs"`; apply on
 * the element that should contain the highlighted text, or pass data via props
 * and use the directive in that component’s template.
 *
 * When `searchQuery` is empty after trim, the host is left unchanged if
 * it already shows `text` with only text child nodes and no `<mark>` children
 * (avoids redundant DOM writes; element children are reset even when text matches).
 */
export const vSearchHighlight = {
  mounted(el: HTMLElement, binding: DirectiveBinding<SearchHighlightDirectiveValue | undefined>) {
    apply(el, binding);
  },
  updated(el: HTMLElement, binding: DirectiveBinding<SearchHighlightDirectiveValue | undefined>) {
    apply(el, binding);
  }
};

export function registerSearchHighlightDirective(app: App): void {
  app.directive('search-highlight', vSearchHighlight);
}
